import { isAxiosError } from 'axios'

/**
 * Extracts the `detail` string from a Django Ninja `CustomValidationError`
 * response (HTTP 422 `UNPROCESSABLE_ENTITY`) so the partner-actionable reason
 * raised on the BE can be rendered at the mutation's action site.
 *
 * Response shape (see `utils/ninja_api/errors.py::error_response`):
 *   { detail: string, code: 'VALIDATION_ERROR', data?: { errors: [...] } }
 *
 * Returns `null` for anything that isn't a 422 with a string `detail` so the
 * inline `MutationError` falls back to its generic localised `messageId`
 * instead of leaking a non-actionable backend string. Sentry observability is
 * untouched — this helper only feeds the user-visible message override.
 *
 * Pair with `useMutationErrorHandler(prefix, { extractDetail })` and
 * `<MutationError detail={state.detail} messageId="..." show={state.hasError} />`.
 */
export const extractValidationDetail = (error: unknown): string | null => {
  if (!isAxiosError(error)) {
    return null
  }
  if (error.response?.status !== 422) {
    return null
  }
  const data: unknown = error.response.data
  if (typeof data !== 'object' || data === null) {
    return null
  }
  const detail = (data as { detail?: unknown }).detail

  return typeof detail === 'string' && detail !== '' ? detail : null
}

export type ValidationFieldError = { field: string; message: string }

const isBlobLike = (value: unknown): value is Blob =>
  typeof value === 'object' && value !== null && typeof (value as Blob).text === 'function'

/**
 * Pulls per-field errors out of a Django Ninja *schema-level* 422 (the Pydantic
 * envelope: `detail: "Invalid request"`, `data.errors: [{ loc, msg, type }]`).
 * `loc` ends at the failing field, so the last segment is the field name a form
 * can hang the message on. Returns `null` for the *service-level*
 * `CustomValidationError` shape (`data.errors: [{ message, code }]`, no `loc`) —
 * those carry no field scope, so the caller falls back to
 * {@link extractValidationDetail} + the inline alert. Sentry observability is
 * unaffected; this only feeds per-field surfacing.
 */
export const extractValidationFieldErrors = (error: unknown): ValidationFieldError[] | null => {
  if (!isAxiosError(error) || error.response?.status !== 422) {
    return null
  }
  const data: unknown = error.response.data
  if (typeof data !== 'object' || data === null) {
    return null
  }
  const errors = (data as { data?: { errors?: unknown } }).data?.errors
  if (!Array.isArray(errors)) {
    return null
  }
  const fieldErrors: ValidationFieldError[] = []
  for (const entry of errors) {
    if (typeof entry !== 'object' || entry === null) {
      continue
    }
    const loc = (entry as { loc?: unknown }).loc
    const msg = (entry as { msg?: unknown }).msg
    if (Array.isArray(loc) && loc.length > 0 && typeof msg === 'string' && msg !== '') {
      const field: unknown = loc[loc.length - 1]
      if (typeof field === 'string') {
        fieldErrors.push({ field, message: msg })
      }
    }
  }

  return fieldErrors.length > 0 ? fieldErrors : null
}

/**
 * Blob-response variant of {@link extractValidationDetail}. An endpoint fetched
 * with `responseType: 'blob'` (e.g. the settlement CSV export) hands even its
 * ERROR body back as a `Blob`, so the sync extractor can't read `.detail`. This
 * reads a 422 blob as text, parses the Ninja `CustomValidationError` envelope,
 * and returns its `detail` string (e.g. the names of partners whose payout
 * details are invalid). Returns `null` for a non-422, a non-blob body, or an
 * unparseable body — the caller falls back to its generic message. The error is
 * NOT swallowed: observability is owned by the caller's `onError` (Sentry),
 * which runs independently of this best-effort detail read.
 */
export const extractBlobValidationDetail = async (error: unknown): Promise<string | null> => {
  if (!isAxiosError(error) || error.response?.status !== 422) {
    return null
  }
  const data: unknown = error.response.data
  // Duck-type on `.text()` rather than `instanceof Blob` — a plain JSON error
  // body has no `.text()`, and this stays correct across Blob realms/polyfills.
  if (!isBlobLike(data)) {
    // Already-parsed JSON error (not a blob) — reuse the sync path.
    return extractValidationDetail(error)
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(await data.text())
  } catch {
    // Non-JSON blob (e.g. an HTML gateway error) — no actionable detail to show.
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null
  }
  const detail = (parsed as { detail?: unknown }).detail

  return typeof detail === 'string' && detail !== '' ? detail : null
}
